import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { 
  GraduationCap, Users, Briefcase, UserCog, 
  Play, Clock, CheckCircle, ArrowLeft, Star,
  FileText, Download, ExternalLink, Shield, Loader2
} from 'lucide-react';
import TrainingPresentation from '../components/TrainingPresentation';
import { adminTrainingSlides } from '../components/training/AdminTrainingSlides';
import { agentTrainingSlides } from '../components/training/AgentTrainingSlides';
import { studentTrainingSlides } from '../components/training/StudentTrainingSlides';
import { employeeTrainingSlides } from '../components/training/EmployeeTrainingSlides';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrainingHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTraining, setActiveTraining] = useState(null);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [completedTrainings, setCompletedTrainings] = useState(() => {
    const saved = localStorage.getItem('completedTrainings');
    return saved ? JSON.parse(saved) : [];
  });

  // Function to download PDF documentation
  const handleDownloadPdf = async (docType, filename) => {
    setDownloadingDoc(docType);
    try {
      // Get token from localStorage since useAuth doesn't expose it directly
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to download documents.');
        setDownloadingDoc(null);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/docs/download/${docType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${filename} downloaded successfully`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document. Please try again.');
    } finally {
      setDownloadingDoc(null);
    }
  };

  // All available trainings
  const allTrainings = [
    {
      id: 'admin',
      title: 'Admin Dashboard Training',
      subtitle: 'Complete guide to managing the platform',
      icon: <UserCog className="w-10 h-10" />,
      color: 'yellow',
      slides: adminTrainingSlides,
      duration: '22 min',
      topics: ['Dashboard Overview', 'Journey Tracking', 'Agent Management', 'Analytics', 'AI Visa Predictor', 'Smart Matching', 'Doc Approval', 'System Health'],
      roles: ['admin'] // Only for admins and employees
    },
    {
      id: 'agent',
      title: 'Agent Partner Training',
      subtitle: 'Learn to manage students and earn commissions',
      icon: <Briefcase className="w-10 h-10" />,
      color: 'green',
      slides: agentTrainingSlides,
      duration: '17 min',
      topics: ['Referral System', 'Adding Students', 'Submitting Applications', 'AI Visa Predictor', 'Smart Matching', 'E-Signature', 'Commission Tracking'],
      roles: ['agent'] // Only for agents
    },
    {
      id: 'student',
      title: 'Student Guide',
      subtitle: 'Your journey to studying abroad',
      icon: <GraduationCap className="w-10 h-10" />,
      color: 'blue',
      slides: studentTrainingSlides,
      duration: '14 min',
      topics: ['Profile Building', 'Course Search', 'Smart Matching', 'Visa Checker', 'Doc Checklist', 'Journey Tracking'],
      roles: ['student'] // Only for students
    },
    {
      id: 'employee',
      title: 'Employee Onboarding',
      subtitle: 'Get started as a team member',
      icon: <Users className="w-10 h-10" />,
      color: 'purple',
      slides: employeeTrainingSlides,
      duration: '18 min',
      topics: ['Permissions', 'Processing Applications', 'AI Visa Predictor', 'Doc Approval', 'Smart Matching', 'Record Payments', 'Best Practices'],
      roles: ['admin'] // For employees (who have admin role)
    }
  ];

  // Filter trainings based on user role
  const trainings = user?.is_super_admin 
    ? allTrainings // Super admin sees all
    : user?.role === 'admin' && user?.employee_id
      ? allTrainings.filter(t => t.id === 'employee' || t.id === 'admin') // Employees see employee & admin training
      : allTrainings.filter(t => t.roles.includes(user?.role)); // Others see only their role's training

  const colorClasses = {
    yellow: {
      bg: 'bg-teal-50',
      border: 'border-teal-200',
      text: 'text-teal-600',
      icon: 'bg-teal-100',
      button: 'bg-teal-600 hover:bg-teal-700 text-white'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-600',
      icon: 'bg-green-100',
      button: 'bg-green-600 hover:bg-green-700 text-white'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-600',
      icon: 'bg-blue-100',
      button: 'bg-blue-600 hover:bg-blue-700 text-white'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-600',
      icon: 'bg-purple-100',
      button: 'bg-purple-600 hover:bg-purple-700 text-white'
    }
  };

  const handleComplete = (trainingId) => {
    const newCompleted = [...new Set([...completedTrainings, trainingId])];
    setCompletedTrainings(newCompleted);
    localStorage.setItem('completedTrainings', JSON.stringify(newCompleted));
    setActiveTraining(null);
  };

  const isCompleted = (trainingId) => completedTrainings.includes(trainingId);

  if (activeTraining) {
    const training = trainings.find(t => t.id === activeTraining);
    return (
      <TrainingPresentation
        slides={training.slides}
        title={training.title}
        onComplete={() => handleComplete(activeTraining)}
        onExit={() => setActiveTraining(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-100/80 via-[#e9ecef] to-[#f1f3f5] border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-teal-100">
              <Play className="w-8 h-8 text-teal-600" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-gray-800 dark:text-white">Training Center</h1>
              <p className="text-gray-500 mt-1">Interactive presentations to help you get started</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border-2 border-gray-200 dark:border-slate-700 transition-colors">
            <p className="text-3xl font-semibold text-gray-800 dark:text-white">{trainings.length}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Available Trainings</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border-2 border-gray-200 dark:border-slate-700 transition-colors">
            <p className="text-3xl font-semibold text-green-600">{trainings.filter(t => completedTrainings.includes(t.id)).length}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Completed</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border-2 border-gray-200 dark:border-slate-700 transition-colors">
            <p className="text-3xl font-semibold text-teal-600">{trainings.filter(t => !completedTrainings.includes(t.id)).length}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Remaining</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border-2 border-gray-200 dark:border-slate-700 transition-colors">
            <p className="text-3xl font-semibold text-blue-600">~{trainings.reduce((sum, t) => sum + parseInt(t.duration), 0)} min</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Duration</p>
          </div>
        </div>

        {/* Training Cards */}
        <div className="grid grid-cols-2 gap-6">
          {trainings.map((training) => {
            const colors = colorClasses[training.color];
            const completed = isCompleted(training.id);

            return (
              <div
                key={training.id}
                className={`bg-white rounded-2xl border-2 ${completed ? 'border-green-400' : 'border-gray-200'} overflow-hidden hover:border-teal-300 transition-all group hover:shadow-md`}
              >
                {/* Header */}
                <div className={`p-6 ${colors.bg}`}>
                  <div className="flex items-start justify-between">
                    <div className={`p-3 rounded-xl ${colors.icon}`}>
                      <div className={colors.text}>{training.icon}</div>
                    </div>
                    {completed && (
                      <div className="flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-medium">
                        <CheckCircle className="w-4 h-4" />
                        <span>Completed</span>
                      </div>
                    )}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800 mt-4">{training.title}</h2>
                  <p className="text-gray-600 mt-1">{training.subtitle}</p>
                </div>

                {/* Body */}
                <div className="p-6">
                  {/* Duration & Slides */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{training.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4" />
                      <span>{training.slides.length} slides</span>
                    </div>
                  </div>

                  {/* Topics */}
                  <div className="space-y-2 mb-6">
                    <p className="text-xs font-medium text-gray-500 uppercase">Topics Covered</p>
                    <div className="flex flex-wrap gap-2">
                      {training.topics.map((topic, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600 font-medium"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Button */}
                  <button
                    onClick={() => setActiveTraining(training.id)}
                    className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${colors.button}`}
                    data-testid={`start-${training.id}-training`}
                  >
                    <Play className="w-5 h-5" />
                    <span>{completed ? 'Watch Again' : 'Start Training'}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Keyboard shortcuts info */}
        <div className="mt-8 p-6 bg-white rounded-xl border border-gray-200">
          <h3 className="font-medium text-gray-800 mb-4">⌨️ Keyboard Shortcuts (During Presentation)</h3>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-teal-600 font-mono border border-gray-200">→</kbd>
              <span className="text-gray-600 dark:text-gray-300">Next slide</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-teal-600 font-mono border border-gray-200">←</kbd>
              <span className="text-gray-600 dark:text-gray-300">Previous slide</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-teal-600 font-mono border border-gray-200">F</kbd>
              <span className="text-gray-600 dark:text-gray-300">Toggle fullscreen</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 rounded text-teal-600 font-mono border border-gray-200">Esc</kbd>
              <span className="text-gray-600 dark:text-gray-300">Exit presentation</span>
            </div>
          </div>
        </div>

        {/* Policy Documents - Only for Employees & Admins */}
        {(user?.is_super_admin || (user?.role === 'admin' && user?.employee_id)) && (
          <div className="mt-8 p-6 bg-white rounded-xl border-2 border-purple-200" data-testid="policy-documents-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-purple-100">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-lg">Policy Documents</h3>
                <p className="text-sm text-gray-500">Important documents for H E Direct team members</p>
              </div>
            </div>
            
            {/* Updated Documentation v2 - Downloadable */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Download className="w-4 h-4" />
                Updated Documentation (v2) - Download
              </h4>
              <div className="space-y-3">
                {/* System Manual v2 - Download */}
                <button
                  onClick={() => handleDownloadPdf('system-manual', 'HE-Direct-System-Manual-v2.pdf')}
                  disabled={downloadingDoc === 'system-manual'}
                  className="w-full flex items-center justify-between p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl border-2 border-emerald-300 transition-all group text-left disabled:opacity-70"
                  data-testid="download-system-manual-v2"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-white border border-emerald-300">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 group-hover:text-emerald-700 transition-colors flex items-center gap-2">
                        System Manual v2
                        <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full">NEW</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Complete CRM documentation with Lead Management, Agent Compliance & more
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-600">
                    {downloadingDoc === 'system-manual' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm font-medium">Downloading...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-medium">Download PDF</span>
                        <Download className="w-4 h-4" />
                      </>
                    )}
                  </div>
                </button>

                {/* Staff Training Guide v2 - Download */}
                <button
                  onClick={() => handleDownloadPdf('staff-training', 'HE-Direct-Staff-Training-Guide-v2.pdf')}
                  disabled={downloadingDoc === 'staff-training'}
                  className="w-full flex items-center justify-between p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl border-2 border-emerald-300 transition-all group text-left disabled:opacity-70"
                  data-testid="download-staff-training-v2"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-white border border-emerald-300">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 group-hover:text-emerald-700 transition-colors flex items-center gap-2">
                        Staff Training Guide v2
                        <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full">NEW</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Updated training guide with HR features, Finance operations & KPI requirements
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-600">
                    {downloadingDoc === 'staff-training' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm font-medium">Downloading...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-medium">Download PDF</span>
                        <Download className="w-4 h-4" />
                      </>
                    )}
                  </div>
                </button>

                {/* Claude AI Operations Playbook - Download */}
                <button
                  onClick={() => handleDownloadPdf('claude-playbook', 'HE-Direct-Claude-Operations-Playbook.pdf')}
                  disabled={downloadingDoc === 'claude-playbook'}
                  className="w-full flex items-center justify-between p-4 bg-indigo-50 hover:bg-indigo-100 rounded-xl border-2 border-indigo-300 transition-all group text-left disabled:opacity-70"
                  data-testid="download-claude-playbook"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-white border border-indigo-300">
                      <Shield className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 group-hover:text-indigo-700 transition-colors flex items-center gap-2">
                        Claude AI Operations Playbook
                        <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full">AI</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Hand this to Claude AI — RACI matrix, 23-tool reference, 9 SOPs & prompt library
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-indigo-600">
                    {downloadingDoc === 'claude-playbook' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm font-medium">Downloading...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-medium">Download PDF</span>
                        <Download className="w-4 h-4" />
                      </>
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Original Documentation - External Links */}
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Original Documents (External)
              </h4>
              <div className="space-y-3">
                {/* Partner Risk & Student Protection Strategy */}
                <a
                  href="https://customer-assets.emergentagent.com/job_uniapply-hub/artifacts/dvea5gzo_H-E-Direct-Partner-Risk-and-Student-Protection-Strategy.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 rounded-xl border border-purple-200 transition-all group"
                  data-testid="policy-doc-partner-risk"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-white border border-purple-200">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 group-hover:text-purple-700 transition-colors">
                        Partner Risk & Student Protection Strategy
                      </p>
                      <p className="text-sm text-gray-500">
                        Guidelines for partner segmentation, risk assessment, and student counselling protocols
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-purple-600">
                    <span className="text-sm font-medium">View PDF</span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                </a>

                {/* System Manual - Original */}
                <a
                  href="https://customer-assets.emergentagent.com/job_globaledudash/artifacts/qck0r8st_HE-Direct-System-Manual%20%282%29.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-200 transition-all group"
                  data-testid="doc-system-manual-original"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-white border border-blue-200">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 group-hover:text-blue-700 transition-colors">
                        System Manual (Original)
                      </p>
                      <p className="text-sm text-gray-500">
                        Legacy CRM documentation
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-blue-600">
                    <span className="text-sm font-medium">View PDF</span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                </a>

                {/* Staff Training Guide - Original */}
                <a
                  href="https://customer-assets.emergentagent.com/job_globaledudash/artifacts/nc03sjpn_HE-Direct-Staff-Training-Guide.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-teal-50 hover:bg-teal-100 rounded-xl border border-teal-200 transition-all group"
                  data-testid="doc-staff-training-original"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-white border border-teal-200">
                      <FileText className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 group-hover:text-teal-700 transition-colors">
                        Staff Training Guide (Original)
                      </p>
                      <p className="text-sm text-gray-500">
                        Legacy training document
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-teal-600">
                    <span className="text-sm font-medium">View PDF</span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                </a>
              </div>
            </div>
            
            <p className="mt-4 text-xs text-gray-400 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              These documents are confidential and only visible to H E Direct employees
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingHub;
